(function () {
  'use strict';
  if (window.WallForgePortraitHistory) return;
  const S = window.WallForgeStudioState;
  const prefix = () => [window.WallForgeAPI.baseUrl, window.Shopify?.shop || location.host, ''].join('/');
  const changed = () => document.dispatchEvent(new CustomEvent('wallforge:history-updated'));
  const encodedPhotos = new WeakMap();
  // File objects are immutable, but picker-backed bytes can disappear when a
  // mobile photo provider releases its handle. Own the bytes before admission.
  async function capturePhoto(file) {
    const encoded = await encodePhotos(file);
    return decodePhotos(encoded);
  }
  function snapshot(value, seen = new Map()) {
    if (!value || typeof value !== 'object' || value instanceof Blob) return value;
    if (seen.has(value)) return seen.get(value);
    if (!Array.isArray(value) && Object.getPrototypeOf(value) !== Object.prototype) return structuredClone(value);
    const copy = Array.isArray(value) ? [] : {}; seen.set(value, copy);
    for (const key of Object.keys(value)) copy[key] = snapshot(value[key], seen);
    return copy;
  }
  async function encodePhotos(value, seen = new Map()) {
    if (!value || typeof value !== 'object') return value;
    if (value instanceof Blob) {
      if (!encodedPhotos.has(value)) {
        const pending = value.arrayBuffer().then(bytes => ({
          __wallforgePhotoBytes: 1, bytes, type: value.type,
          name: value instanceof File ? value.name : null,
          lastModified: value instanceof File ? value.lastModified : null
        })).catch(error => { encodedPhotos.delete(value); throw error; });
        encodedPhotos.set(value, pending);
      }
      return encodedPhotos.get(value);
    }
    if (!Array.isArray(value) && Object.getPrototypeOf(value) !== Object.prototype) return value;
    if (seen.has(value)) return seen.get(value);
    const copy = Array.isArray(value) ? [] : {}; seen.set(value, copy);
    for (const key of Object.keys(value)) copy[key] = await encodePhotos(value[key], seen);
    return copy;
  }
  function decodePhotos(value, seen = new Map()) {
    if (!value || typeof value !== 'object') return value;
    if (seen.has(value)) return seen.get(value);
    if (value.__wallforgePhotoBytes === 1 && value.bytes instanceof ArrayBuffer) {
      const photo = typeof value.name === 'string'
        ? new File([value.bytes], value.name, { type: value.type, lastModified: value.lastModified })
        : new Blob([value.bytes], { type: value.type });
      encodedPhotos.set(photo, Promise.resolve(value));
      seen.set(value, photo); return photo;
    }
    if (!Array.isArray(value) && Object.getPrototypeOf(value) !== Object.prototype) return value;
    const copy = Array.isArray(value) ? [] : {}; seen.set(value, copy);
    for (const key of Object.keys(value)) copy[key] = decodePhotos(value[key], seen);
    return copy;
  }
  let database;
  function storageError(error) {
    const failure = new Error('Portrait history storage is temporarily unavailable');
    failure.name = error?.name || 'AbortError';
    failure.code = 'history_storage_unavailable';
    return failure;
  }
  function reportStorageFailure(failure, mode) {
    try { window.Sentry?.captureException(failure, { tags: { component: 'portrait-history', operation: mode } }); }
    catch (_) { /* Monitoring must never break recovery. */ }
  }
  function db() {
    if (!database) {
      const opening = new Promise((resolve, reject) => {
        const request = indexedDB.open('wallforge-studio', 1);
        request.onupgradeneeded = () => request.result.createObjectStore('designs');
        request.onsuccess = () => {
          const connection = request.result;
          const invalidate = () => { if (database === opening) database = null; };
          connection.onclose = invalidate;
          connection.onversionchange = () => { invalidate(); connection.close(); };
          resolve(connection);
        };
        request.onerror = () => { if (database === opening) database = null; reject(storageError(request.error)); };
      });
      database = opening;
    }
    return database;
  }
  async function transact(mode, operation) {
    for (let attempt = 0; attempt < 2; attempt++) {
      let connection;
      try {
        connection = await db();
        return await new Promise((resolve, reject) => {
          const transaction = connection.transaction('designs', mode);
          let requestError, result;
          transaction.onerror = event => { requestError = event.target?.error; };
          transaction.onabort = () => reject(storageError(transaction.error || requestError));
          transaction.oncomplete = () => resolve(result?.());
          result = operation(transaction.objectStore('designs'));
        });
      } catch (error) {
        const failure = storageError(error);
        if (attempt === 0 && ['AbortError', 'UnknownError', 'InvalidStateError'].includes(failure.name)) {
          // Reopen a stale/interrupted connection once; never erase retained work.
          database = null; connection?.close();
          continue;
        }
        reportStorageFailure(failure, mode);
        throw failure;
      }
    }
  }
  async function storage(key, value) {
    // WebKit can abort Blob/File writes with transaction.error === null. Store
    // the exact bytes instead, then restore File objects at the UI boundary.
    // Conversion happens before opening a transaction, never during one.
    let encoded;
    try { encoded = value === undefined ? undefined : await encodePhotos(value); }
    catch (error) { const failure = storageError(error); reportStorageFailure(failure, 'encode'); throw failure; }
    const result = await transact(value === undefined ? 'readonly' : 'readwrite', store => {
      const request = value === undefined ? store.get(key) : store.put(encoded, key);
      return () => request.result;
    });
    if (value !== undefined) changed();
    return value === undefined ? decodePhotos(result) : result;
  }
  async function groups(preferred) {
    const scope = prefix();
    const records = await transact('readonly', store => {
      const request = store.openCursor(), records = [];
      request.onsuccess = () => {
        const cursor = request.result; if (!cursor) return;
        const state = decodePhotos(cursor.value);
        if (state?.designId && String(cursor.key) === scope + state.designId) records.push({ key: cursor.key, state });
        cursor.continue();
      };
      return () => records;
    });
    return S.libraryGroups(records, preferred);
  }
  const safeImage = value => { if (typeof value !== 'string' || !value.trim()) return ''; try { const url = new URL(value, location.origin); return /^(https?:)$/.test(url.protocol) ? url.href : ''; } catch (_) { return ''; } };
  function portraitUrl(group, id) {
    const url = new URL((window.Shopify?.routes?.root || '/') + 'products/' + encodeURIComponent(group.design.handle), location.origin);
    url.searchParams.set('wf_portrait', id);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url.href;
  }
  async function patchVersion(group, original, patch) {
    await transact('readwrite', store => {
      const request = store.get(group.key);
      request.onsuccess = () => {
        const state = request.result;
        const version = String(state?.designId) === group.designId && state.versions?.find(v => v.id === original.id && v.jobId === original.jobId);
        if (version && version.capabilities?.uploadStatus?.token === original.capabilities?.uploadStatus?.token) {
          Object.assign(version, patch); store.put(state, group.key);
        }
      };
    });
    changed();
  }
  const refreshes = new Map();
  const refreshQueue = [];
  let refreshing = 0;
  function enqueueRefresh(run) {
    return new Promise((resolve, reject) => {
      refreshQueue.push({ run, resolve, reject }); drainRefreshes();
    });
  }
  function drainRefreshes() {
    while (refreshing < 2 && refreshQueue.length) {
      const next = refreshQueue.shift(); refreshing++;
      Promise.resolve().then(next.run).then(next.resolve, next.reject).finally(() => { refreshing--; drainRefreshes(); });
    }
  }
  function needsRefresh(version) {
    const expires = Date.parse(version.previewExpiresAt || '');
    return !version.previewRefreshedAt || Date.now() - version.previewRefreshedAt >= 45 * 60000
      || (Number.isFinite(expires) && expires <= Date.now() + 60000);
  }
  async function currentVersion(group, item) {
    const state = await storage(group.key);
    return String(state?.designId) === group.designId ? S.completedVersions(state).find(v => v.id === item.version.id && v.jobId === item.version.jobId) : null;
  }
  async function refresh(group, item, force = false) {
    const version = item.version, identity = group.key + '/' + version.id;
    if (refreshes.has(identity)) return refreshes.get(identity);
    if (!force && !needsRefresh(version)) return;
    const run = async () => {
      // A queued request may have been renewed by another surface in the meantime.
      const current = await currentVersion(group, item);
      if (!current || (!force && !needsRefresh(current))) return;
      const studio = document.querySelector('[data-wf-studio]')?.studio;
      if (studio?.ready && studio.key === group.key) {
        const active = S.completedVersions(studio.state).find(v => v.id === version.id);
        if (active) return studio.refreshVersion(active, true);
      }
      const credential = current.capabilities?.uploadStatus?.token;
      if (!credential) return;
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 15000);
      try {
        const url = new URL(window.WallForgeAPI.endpoints.status + '/' + encodeURIComponent(version.jobId));
        url.searchParams.set('revisionId', version.id); url.searchParams.set('capabilityToken', credential);
        const response = await fetch(url.href, { cache: 'no-store', credentials: 'same-origin', signal: controller.signal });
        const data = await response.json();
        if (response.status === 404 || response.status === 410) { await patchVersion(group, current, { unavailable: true }); return; }
        if (!response.ok || data.jobId !== version.jobId || data.artwork?.revisionId !== version.id || !data.artwork?.binding || !safeImage(data.previewUrl)) return;
        await patchVersion(group, current, { previewUrl: safeImage(data.previewUrl), cartPreviewUrl: safeImage(data.cartPreviewUrl),
          previewExpiresAt: data.tokens?.preview?.expiresAt || null, previewRefreshedAt: Date.now(),
          binding: data.artwork.binding, printRef: data.printRef || version.printRef,
          capabilities: Object.assign({}, current.capabilities, data.capabilities) });
      } catch (_) { /* Temporary failures never discard retained history. */ }
      finally { clearTimeout(timer); }
    };
    const promise = enqueueRefresh(run); refreshes.set(identity, promise);
    try { await promise; } finally { refreshes.delete(identity); }
  }
  async function refreshGroups(items) {
    const queue = items.flatMap(group => group.versions.map(item => ({ group, item })));
    await Promise.all([0, 1].map(async () => { while (queue.length) { const next = queue.shift(); await refresh(next.group, next.item); } }));
  }
  const count = items => items.reduce((sum, group) => sum + group.versions.length, 0);
  window.WallForgePortraitHistory = { storage, snapshot, capturePhoto, groups, refresh, refreshGroups, needsRefresh, currentVersion, count, portraitUrl, safeImage };
})();
